//
// # Move Mode Handler(s)
//
// There are a few, depending on whether one can change orientations (and how) while moving.
//

// Local Imports
import { logger } from "CoreUtils";
import {
  layout,
  Direction,
  Rotation,
  Xform,
  Point,
  schdata,
  graphics,
  model,
} from "GenAlphaModel";
import { Keys } from "../keys";
import * as changes from "DrawCore/src/changes";
import type { InteractionHost } from "../interaction_host";
import { EntityKind } from "DrawCore/src/entity";
import { ModeHandler } from "./base";
import {
  computeMovedDelta,
  computeMovedLoc,
  computeMovedToGrid,
  DragThreshold,
} from "./axis_constrain";
import type { InstanceEntity } from "DrawCore/src/content/layout/instance";
import { PinEntity } from "DrawCore/src/content/layout/pin";
import { LayoutContent } from "../content/layout/content";
import { Schematic } from "../content/schematic/schematic";
import { WireSegment } from "DrawCore/src/content/schematic/wire";
import { Instance, SchPin } from "DrawCore/src/content/schematic/instance";
import { Annotation } from "DrawCore/src/content/shared";
import { pointKey, getSchPinWorldLocation } from "DrawCore/src/routing";
import { components, planWireEdits } from "DrawCore/src/routing/wire_plan";
import type { NetPin } from "DrawCore/src/routing/connectivity";
import {
  applyWireEdits,
  auditWirePlan,
  mergeOverlaps,
  snapshotWires,
} from "./wire_edits";

// Entities that have a `loc` which can be updated
export interface Movable {
  loc: Point;
  update(data: { loc: Point }): unknown;
}

abstract class MoveBase<Target, Data> extends ModeHandler {
  override movesGeometryLive = true;

  constructor(
    readonly editor: InteractionHost, // Root/ parent editor
    readonly target: Target, // The entity being moved
    readonly orig: Data, // The quantity being tracked. Generally either a location or transform.
    // The target's original location in schematic coordinates. Combined with
    // initialMousePos, this lets updateMove() recover the raw cursor delta and
    // apply shift-to-constrain before snapping. Avoids subclasses needing to
    // pre-compute "mouse relative to target" — they already know origLoc.
    readonly origLoc: Point,
    // The initial cursor position in schematic coordinates at the moment the
    // move began. Together with origLoc this captures the click-grab offset
    // implicitly (origLoc - initialMousePos), so the target does not jump on
    // the first mousemove.
    readonly initialMousePos: Point
  ) {
    super();
    this.dragThreshold = new DragThreshold(
      Point.clone(editor.uiState.mousePos.page)
    );
  }

  // Suppresses movement until the cursor leaves a small pixel radius around the
  // press. Every MoveBase mode is entered from a mouse-press (see the various
  // `enter` sites), so the press point is the cursor's page position at
  // construction time. Prevents a plain click from nudging the target.
  private readonly dragThreshold: DragThreshold;

  // Stuff to be implemented by the concrete classes
  abstract logChange(): unknown; // Log the change to the change log
  abstract updateLoc(loc: Point): unknown; // Update the target's location
  abstract updateData(data: Data): unknown; // Update the target's ("total") data

  // Default implementations
  override handleKey = (e: KeyboardEvent) => {}; // Handle keystrokes. No-op.
  override handleMouseUp = () => this.commit(); // Commit the move on mouse-up
  override handleMouseMove = () => this.updateMove(); // Update the rendering of the instance on mouse-move.

  // "Commit" a move by placing it in the change log.
  // Actual moves and re-draws are performed "live" as the mouse moves.
  readonly commit = () => {
    this.logChange();
    this.editor.goUiIdle();
  };

  // Update the rendering of an in-progress instance move.
  readonly updateMove = () => {
    const { editor, initialMousePos, origLoc } = this;
    const { mousePos, modifiers } = editor.uiState;
    // Ignore sub-threshold cursor motion so a plain click doesn't move the target.
    if (!this.dragThreshold.begun(mousePos.page)) return;
    const newLoc = computeMovedLoc({
      origLoc,
      initialMousePos,
      currentMousePos: mousePos.sch,
      shift: modifiers.shift,
      snap: editor.grid.nearestPoint,
    });
    return this.updateLoc(newLoc);
  };

  // On abort, send our target back to its original location.
  override abort = () => {
    const { editor, orig } = this;
    this.updateData(orig);
    editor.deselect();
    editor.goUiIdle();
  };
}

export class Move extends MoveBase<Movable, Point> {
  override updateData = (data: Point): unknown => this.updateLoc(data);

  override updateLoc = (loc: Point): unknown => {
    return this.target.update({ loc });
  };

  // Enter this mode
  static enter(editor: InteractionHost, target: Movable): unknown {
    const orig = Point.clone(target.loc);
    const initialMousePos = Point.clone(editor.uiState.mousePos.sch);
    // For `Move`, orig *is* the original location, so we pass it as both
    // the tracked data (`orig`) and the location anchor (`origLoc`).
    const move = new Move(editor, target, orig, orig, initialMousePos);
    return editor.setMode(move);
  }

  // "Commit" a move by placing it in the change log.
  // Actual moves and re-draws are performed "live" as the mouse moves.
  logChange = () => {
    const { editor, target, orig } = this;
    if (Point.eq(orig, target.loc)) return;

    // Notify the changeLog and platform of the change.
    editor.content.logChange(
      changes.update({
        target,
        from: { loc: orig },
        to: { loc: Point.clone(target.loc) },
      })
    );
  };
}

// Interface for graphics elements that can be moved as a whole shape.
// Unlike Movable (which records {loc} deltas), MoveGraphics records full element data
// snapshots so the server receives complete core.Element data for every update.
export interface GraphicsMovable {
  loc: Point;
  data: object;
  updateLoc(loc: Point): unknown;
  update(data: Partial<never>): unknown;
}

export class MoveGraphics extends MoveBase<GraphicsMovable, object> {
  private constructor(
    editor: InteractionHost,
    target: GraphicsMovable,
    origData: object,
    origLoc: Point,
    initialMousePos: Point
  ) {
    super(editor, target, origData, origLoc, initialMousePos);
  }

  override updateLoc = (loc: Point): unknown => this.target.updateLoc(loc);
  override updateData = (data: Partial<never>): unknown =>
    this.target.update(data);

  static enter(editor: InteractionHost, target: GraphicsMovable): unknown {
    const origLoc = Point.clone(target.loc);
    const origData = { ...target.data };
    const initialMousePos = Point.clone(editor.uiState.mousePos.sch);
    return editor.setMode(
      new MoveGraphics(editor, target, origData, origLoc, initialMousePos)
    );
  }

  // Log the change with full element data snapshots for server serialization.
  // Safety: `any` is used because GraphicsMovable.data is typed as `object` to avoid
  // coupling to specific graphics element types; the actual values are schema element data.
  logChange = (): unknown => {
    const { editor, target, orig: origData, origLoc } = this;
    if (Point.eq(origLoc, target.loc)) return; // No change
    editor.content.logChange(
      changes.update({
        target: target as changes.Updatable<unknown>,
        from: origData,
        to: { ...target.data },
      })
    );
  };
}

// Move multiple graphics elements as a group.
// Extends MoveBase with arrays: target is the list of GraphicsMovable,
// orig is the list of snapshot data blobs. Mouse deltas apply to all simultaneously.
// Changes are logged as a single Combo so undo reverts the entire group move.
interface GraphicsGroupEntry {
  target: GraphicsMovable;
  origData: object;
  origLoc: Point;
}

export class MoveGraphicsGroup extends MoveBase<
  Array<GraphicsMovable>,
  Array<object>
> {
  private readonly entries: Array<GraphicsGroupEntry>;

  private constructor(
    editor: InteractionHost,
    entries: Array<GraphicsGroupEntry>,
    refOrigLoc: Point,
    initialMousePos: Point
  ) {
    super(
      editor,
      entries.map((e) => e.target),
      entries.map((e) => e.origData),
      refOrigLoc,
      initialMousePos
    );
    this.entries = entries;
  }

  static enter(
    editor: InteractionHost,
    targets: Array<GraphicsMovable>
  ): unknown {
    if (targets.length === 0) return null;
    const initialMousePos = Point.clone(editor.uiState.mousePos.sch);
    // Use the first target's location as the reference anchor for the move;
    // per-target offsets are recovered in updateLoc via each entry.origLoc.
    const refOrigLoc = Point.clone(targets[0].loc);
    const entries: Array<GraphicsGroupEntry> = targets.map((t) => ({
      target: t,
      origData: { ...t.data },
      origLoc: Point.clone(t.loc),
    }));
    return editor.setMode(
      new MoveGraphicsGroup(editor, entries, refOrigLoc, initialMousePos)
    );
  }

  // updateLoc receives the snapped absolute position for the reference target.
  // Compute the delta from the reference target's original position and apply to all.
  override updateLoc = (loc: Point): unknown => {
    const refOrigLoc = this.entries[0].origLoc;
    const delta = Point.sub(loc, refOrigLoc);
    for (const { target, origLoc } of this.entries) {
      target.updateLoc({
        x: origLoc.x + delta.x,
        y: origLoc.y + delta.y,
      });
    }
    return null;
  };

  // Restore all targets to their original data (used by abort).
  override updateData = (origDataArray: Array<object>): unknown => {
    for (let i = 0; i < this.entries.length; i++) {
      this.entries[i].target.update(origDataArray[i] as Partial<never>);
    }
    return null;
  };

  // Log all element changes as a single atomic Combo change.
  logChange = (): unknown => {
    const { editor, entries } = this;
    const singleChanges = [];

    for (const { target, origData, origLoc } of entries) {
      if (Point.eq(origLoc, target.loc)) continue; // No change for this element
      singleChanges.push(
        changes.update({
          target: target as changes.Updatable<unknown>,
          from: origData,
          to: { ...target.data },
        })
      );
    }

    if (singleChanges.length === 1) {
      editor.content.logChange(singleChanges[0] as changes.Change<never>);
    } else if (singleChanges.length > 1) {
      const combo = changes.Combo.fromArray(singleChanges);
      if (combo.ok)
        editor.content.logChange(combo.val as changes.Change<never>);
    }
    return null;
  };
}

// # MoveHandle
//
// Drags a *handle* — a pin shape/label, a graphics vertex/corner, or a
// selection-box corner — whose live movement is applied via the handle's own
// `update({ loc })`, but whose committed change targets the handle's *owning*
// entity and records that entity's full data.
//
// This is required for symbol entities whose position lives inside their
// geometry (a pin's `shape`, a polygon's `points`, a rect/ellipse `bbox`)
// rather than in a `loc` field. Plain `Move` records only a `{ loc }` delta
// against the handle, which (a) is dropped entirely by the change pipeline
// when the handle's `entityKind` isn't a recognised target kind, and (b) even
// when it isn't dropped, only merges a stray `loc` into the stored entity
// without touching its geometry — so the edit never persists and the design is
// never marked dirty. Recording the owner's full data (as `MoveGraphics` does
// for whole-shape graphics moves) round-trips correctly. A *deep* clone is used
// for the snapshots because some handles (polygon/path vertices) mutate the
// owner's data arrays in place.
export interface MoveHandleTarget {
  loc: Point;
  update(data: { loc: Point }): unknown;
}

// The entity that owns a handle and acts as the logged change target. Its
// `entityKind` selects the change-pipeline target kind and its `data` (with an
// `id`) is serialized to the server.
export interface MoveHandleOwner {
  readonly entityKind: EntityKind;
  readonly data: object;
  update(data: object): unknown;
}

export class MoveHandle extends MoveBase<MoveHandleTarget, Point> {
  private constructor(
    editor: InteractionHost,
    handle: MoveHandleTarget,
    // The owning entity whose full data is snapshotted and logged.
    private readonly owner: MoveHandleOwner,
    // Deep clone of the owner's data captured before the move began.
    private readonly origOwnerData: object,
    origLoc: Point,
    initialMousePos: Point
  ) {
    // `orig` and `origLoc` are both the handle's start location; the tracked
    // "data" for this mode is just the location (used for the no-op check).
    super(editor, handle, origLoc, origLoc, initialMousePos);
  }

  static enter(
    editor: InteractionHost,
    handle: MoveHandleTarget,
    owner: MoveHandleOwner
  ): unknown {
    const origLoc = Point.clone(handle.loc);
    const origOwnerData = globalThis.structuredClone(owner.data);
    const initialMousePos = Point.clone(editor.uiState.mousePos.sch);
    return editor.setMode(
      new MoveHandle(
        editor,
        handle,
        owner,
        origOwnerData,
        origLoc,
        initialMousePos
      )
    );
  }

  // Live drag: apply the new location through the handle (resize/translate).
  override updateLoc = (loc: Point): unknown => this.target.update({ loc });

  // Restore the handle to a given location. MoveBase's default `abort` calls
  // this, but we override `abort` to restore full owner data instead; kept for
  // interface completeness.
  override updateData = (loc: Point): unknown => this.target.update({ loc });

  // On abort, restore the owner's full original data.
  override abort = () => {
    this.owner.update(this.origOwnerData);
    this.editor.deselect();
    this.editor.goUiIdle();
  };

  // Commit: log a full-data update against the owning entity so the change
  // serializes to the server and marks the design dirty.
  override logChange = (): void => {
    if (Point.eq(this.orig, this.target.loc)) return; // No movement.
    this.editor.content.logChange(
      changes.update({
        target: this.owner,
        from: this.origOwnerData,
        to: globalThis.structuredClone(this.owner.data),
      })
    );
  };
}

// Entities which have a location + orientation (`Xform`)
// as part of their data, and can be updated with a new `Xform`.
interface Xformable extends schdata.Xformable {
  xform: Xform;
  update(data: { xform: Xform }): unknown;
}

export class MoveXformable extends MoveBase<Xformable, Xform> {
  //
  // Enter this mode
  static enter(editor: InteractionHost, target: Xformable) {
    const orig = Xform.clone(target.xform);
    const initialMousePos = Point.clone(editor.uiState.mousePos.sch);
    // Xform extends Point, so it doubles as the original-location anchor.
    const origLoc: Point = { x: orig.x, y: orig.y };
    const move = new MoveXformable(
      editor,
      target,
      orig,
      origLoc,
      initialMousePos
    );
    return editor.setMode(move);
  }

  override updateData = (xform: Xform): unknown => {
    return this.target.update({ xform });
  };

  override updateLoc = (loc: Point): unknown => {
    const { target } = this;
    return target.update({ xform: { ...target.xform, ...loc } });
  };

  override logChange = () => {
    const { editor, target, orig } = this;
    const to = target.xform;
    if (Xform.eq(orig, to)) return; // No change

    // Notify the changeLog and platform of the change.
    editor.content.logChange(
      changes.update({
        target: target,
        from: { xform: orig },
        to: { xform: to },
      })
    );
  };

  // Handle keystrokes.
  override handleKey = (e: KeyboardEvent) => {
    const { target } = this;

    // All other UI states: check for "command" keystrokes.
    switch (e.key) {
      // FIXME: do we want deletion/ removal in this state? None for now.
      //   case Keys.Delete:
      //   case Keys.Backspace: {
      //     // Delete the selected target
      //     return editor.deleteSelected();
      //   }
      // Rotation & refelection
      // Note these *are not* commited to the changelog.
      // Mid-move rotations or reflections "count as part of" the move in this sense.
      case Keys.r:
        return target.rotate();
      case Keys.v:
        return target.flip(Direction.Vert);
      case Keys.h:
        return target.flip(Direction.Horiz);
      default:
        // Note this *is not* an exhaustive check, on purpose.
        // There's lots of keys we don't care about!
        // Some day the logging should go away too.
        logger.log(`Key we dont use: '${e.key}'`);
    }
  };
}
// Entities which have a location + orientation (`Xform`)
// as part of their data, and can be updated with a new `Xform`.
interface LayoutXformable {
  getXform(): layout.LayoutXform;
  updateXform(xform: layout.LayoutXform): unknown;
  update(data: {
    place: { kind: "scalar"; xform: layout.LayoutXform };
  }): unknown;
}

// Abstraction for a movable entity snapshot during multi-selection move.
// Handles both schematic entities (with xform: {x, y}) and layout entities
// (with getXform()/updateXform() using LayoutXform).
interface MoveSnapshot {
  // Get the current location as a Point.
  getLoc(): Point;
  // Move to a new location.
  moveTo(loc: Point): unknown;
  // Log a change for undo/redo. Returns null if no change occurred.
  logChange(): changes.Single<changes.ChangeEntity> | null;
  // Restore to original position.
  restore(): void;
  // True iff the snapshot wraps a wire — used by MoveSelection.commit to
  // know it should refresh junction dots. Optional so other snapshot
  // factories don't have to set it.
  isWire?: boolean;
}

// Create a snapshot for a schematic-style entity (xform with x, y).
function schematicSnapshot(entity: {
  xform: Xform;
  update(data: { xform: Xform }): unknown;
}): MoveSnapshot {
  const origXform = Xform.clone(entity.xform);
  return {
    getLoc: () => ({ x: entity.xform.x, y: entity.xform.y }),
    moveTo: (loc: Point) =>
      entity.update({ xform: { ...entity.xform, x: loc.x, y: loc.y } }),
    logChange: () => {
      const currentXform = Xform.clone(entity.xform);
      if (Xform.eq(origXform, currentXform)) return null;
      return changes.update({
        target: entity,
        from: { xform: origXform },
        to: { xform: currentXform },
      });
    },
    restore: () => entity.update({ xform: origXform }),
  };
}

// Create a snapshot for a layout-style entity (LayoutXformable with getXform/updateXform).
function layoutSnapshot(entity: LayoutXformable): MoveSnapshot {
  const xform = entity.getXform();
  const origXform = { ...xform, loc: { ...xform.loc } };
  return {
    getLoc: () => entity.getXform().loc,
    moveTo: (loc: Point) => {
      const xform = entity.getXform();
      entity.updateXform({ ...xform, loc });
    },
    logChange: () => {
      const currentXform = entity.getXform();
      if (layout.LayoutXform.eq(origXform, currentXform)) return null;
      return changes.update({
        target: entity,
        from: { place: { kind: "scalar" as const, xform: origXform } },
        to: { place: { kind: "scalar" as const, xform: currentXform } },
      });
    },
    restore: () => entity.updateXform(origXform),
  };
}

// Create a snapshot for a segmented layout instance.
// Moves all segments together by the same delta.
// Uses InstanceEntity's typed `inner` discriminated union to access segment data
// without `as unknown as` casts.
function segmentedSnapshot(instance: InstanceEntity): MoveSnapshot | null {
  const { inner } = instance;
  if (inner.kind !== "segments" && inner.kind !== "array") return null;
  // After narrowing, inner.drawer is SegmentedInstanceEntity with typed
  // data.place.segments. This reference remains valid across moveTo calls
  // because InstanceEntity.update shallow-copies data but preserves the
  // place reference.
  const drawer = inner.drawer;
  const { segments } = drawer.data.place;

  // For "array" kind instances, the SegmentedInstanceEntity holds a separate
  // copy of the data (converted from array format via ArrayXform.toSegments).
  // Calling instance.update(instance.data) would recreate the inner drawer
  // from the original (unmutated) array data, resetting any position changes
  // and orphaning the segments reference this closure holds.
  // Use the drawer's own update (which just sets data and requests redraw)
  // to keep the mutated segments alive.
  const isArray = inner.kind === "array";

  const origSegLocs = segments.map((s) =>
    s ? { x: s.loc.x, y: s.loc.y } : null
  );
  // Use first segment's loc as the representative position
  const firstSeg = segments.find((s) => s !== null);
  const origLoc = firstSeg
    ? { x: firstSeg.loc.x, y: firstSeg.loc.y }
    : { x: 0, y: 0 };

  return {
    getLoc: () => {
      const seg = segments.find((s) => s !== null);
      return seg ? seg.loc : { x: 0, y: 0 };
    },
    moveTo: (loc: Point) => {
      const dx = loc.x - origLoc.x;
      const dy = loc.y - origLoc.y;
      for (let i = 0; i < segments.length; i++) {
        const seg = segments[i];
        const orig = origSegLocs[i];
        if (seg && orig) {
          seg.loc = { x: orig.x + dx, y: orig.y + dy };
        }
      }
      if (isArray) {
        drawer.update(drawer.data);
      } else {
        instance.update(instance.data);
      }
    },
    logChange: () => {
      const moved = segments.some((seg, i) => {
        const orig = origSegLocs[i];
        return seg && orig && (seg.loc.x !== orig.x || seg.loc.y !== orig.y);
      });
      if (!moved) return null;
      return changes.update({
        target: instance,
        from: {
          place: {
            kind: "segments" as const,
            segments: origSegLocs.map((loc, i) => {
              const seg = segments[i];
              return seg && loc ? { ...seg, loc: { ...loc } } : null;
            }),
          },
        },
        to: {
          place: {
            kind: "segments" as const,
            segments: segments.map((seg) =>
              seg ? { ...seg, loc: { ...seg.loc } } : null
            ),
          },
        },
      });
    },
    restore: () => {
      for (let i = 0; i < segments.length; i++) {
        const seg = segments[i];
        const orig = origSegLocs[i];
        if (seg && orig) {
          seg.loc = { x: orig.x, y: orig.y };
        }
      }
      if (isArray) {
        drawer.update(drawer.data);
      } else {
        instance.update(instance.data);
      }
    },
  };
}

// Create a snapshot for a schematic wire segment. Translates both endpoints
// by the same delta, so the wire's shape and axis are preserved. The wire's
// notional "location" is the first endpoint; moves are computed as a delta
// from the original first endpoint and applied to both endpoints. Adjacent
// connectivity is intentionally NOT preserved here — multi-wire G drops
// connections to non-selected items, consistent with rigid group-move
// semantics (single-wire slide is dispatched separately to MoveWireSegment).
function wireSnapshot(wire: WireSegment): MoveSnapshot {
  const origEndpoints: [Point, Point] = [
    Point.clone(wire.data.endpoints[0]),
    Point.clone(wire.data.endpoints[1]),
  ];
  // Labels ride along with the wire: their `loc` is recomputed from the wire's
  // geometry on every move, so a label always sits where a freshly-added label
  // would (see `model.schematic.recenterLabels`).
  const origLabels = model.schematic.cloneWireLabels(wire.data.labels);
  return {
    isWire: true,
    getLoc: () => ({ x: origEndpoints[0].x, y: origEndpoints[0].y }),
    moveTo: (loc: Point) => {
      const dx = loc.x - origEndpoints[0].x;
      const dy = loc.y - origEndpoints[0].y;
      const newStart = {
        x: origEndpoints[0].x + dx,
        y: origEndpoints[0].y + dy,
      };
      const newEnd = { x: origEndpoints[1].x + dx, y: origEndpoints[1].y + dy };
      wire.update({
        endpoints: [newStart, newEnd],
        labels: model.schematic.recenterLabels(origLabels, newStart, newEnd),
      });
    },
    logChange: () => {
      const current: [Point, Point] = [
        Point.clone(wire.data.endpoints[0]),
        Point.clone(wire.data.endpoints[1]),
      ];
      if (
        Point.eq(origEndpoints[0], current[0]) &&
        Point.eq(origEndpoints[1], current[1])
      ) {
        return null;
      }
      return changes.update({
        target: wire,
        from: { endpoints: origEndpoints, labels: origLabels },
        to: {
          endpoints: current,
          labels: model.schematic.recenterLabels(
            origLabels,
            current[0],
            current[1]
          ),
        },
      });
    },
    restore: () =>
      wire.update({
        endpoints: [
          Point.clone(origEndpoints[0]),
          Point.clone(origEndpoints[1]),
        ],
        labels: model.schematic.cloneWireLabels(origLabels),
      }),
  };
}

// Create a snapshot for a `loc`-based entity (e.g. a text annotation).
// Rigid translation of a single anchor point.
function locSnapshot(entity: Movable & changes.ChangeEntity): MoveSnapshot {
  const origLoc = Point.clone(entity.loc);
  return {
    getLoc: () => ({ x: entity.loc.x, y: entity.loc.y }),
    moveTo: (loc: Point) => entity.update({ loc }),
    logChange: () => {
      const currentLoc = Point.clone(entity.loc);
      if (Point.eq(origLoc, currentLoc)) return null;
      return changes.update({
        target: entity,
        from: { loc: origLoc },
        to: { loc: currentLoc },
      });
    },
    restore: () => entity.update({ loc: origLoc }),
  };
}

// Create a snapshot for a layout pin: rigid translation of all its shapes,
// tracked by its anchor (the centre of its shapes' box).
function pinSnapshot(pin: PinEntity): MoveSnapshot {
  const origLoc = pin.anchor;
  const origShapes = pin.data.shapes;
  return {
    getLoc: () => pin.anchor,
    moveTo: (to: Point) => pin.update({ shapes: pin.shapesAnchoredAt(to) }),
    logChange: () => {
      if (Point.eq(origLoc, pin.anchor)) return null;
      return changes.update({
        target: pin,
        from: { shapes: origShapes },
        to: { shapes: pin.data.shapes },
      });
    },
    restore: () => pin.update({ shapes: origShapes }),
  };
}

// Try to create a MoveSnapshot for any entity in the selection.
// Returns null if the entity is not movable.
// Uses the `entityKind` discriminant (present on all Entity subclasses)
// to determine the entity type and the appropriate snapshot strategy.
// When `selectedIndices` is provided and the entity is segmented, only those
// segments are moved; otherwise all segments move together.
function trySnapshot(
  entity: unknown,
  selectedIndices?: Set<number> | null
): MoveSnapshot | null {
  // Safety: All Entity subclasses in the selection set have entityKind discriminant.
  // This cast is safe because entity comes from editor.content.selection which only
  // contains Entity instances.
  const e = entity as { entityKind: EntityKind };

  switch (e.entityKind) {
    // Layout InstanceEntity: reach into inner.drawer for the LayoutXformable.
    // Only scalar instances are movable (segmented instances don't expose getXform).
    case EntityKind.LayoutInstance: {
      // Safety: entity is an InstanceEntity from the selection set.
      // Use the typed `inner` discriminated union for safe narrowing.
      const inst = entity as InstanceEntity;
      if (inst.inner.kind === "scalar") {
        // Safety: ScalarInstanceEntity structurally satisfies LayoutXformable
        // (has getXform, updateXform, update methods with matching signatures).
        const drawer = inst.inner.drawer as LayoutXformable;
        return layoutSnapshot(drawer);
      }
      if (inst.inner.kind === "segments" || inst.inner.kind === "array") {
        // Use per-segment snapshot if specific indices are selected
        if (selectedIndices) {
          return selectedSegmentSnapshot(inst, selectedIndices);
        }
        return segmentedSnapshot(inst);
      }
      return null;
    }
    // Schematic entities with an Xform (location + orientation).
    case EntityKind.Instance:
    case EntityKind.SchPin: {
      return schematicSnapshot(
        entity as {
          xform: Xform;
          update(data: { xform: Xform }): unknown;
        }
      );
    }
    // Layout pin: rigid translation of its shapes.
    case EntityKind.LayoutPin: {
      if (entity instanceof PinEntity) return pinSnapshot(entity);
      return null;
    }
    // Schematic wire segment: rigid translation of both endpoints.
    case EntityKind.WireSegment: {
      // The other cases above recover the concrete type via `as` casts; TS
      // does not narrow `entity` based on `e.entityKind` because `e` is a
      // separately-cast alias. Those casts are grandfathered by lint:diff,
      // but new code must avoid them — so this branch uses a runtime
      // `instanceof` check instead. (The longer-term cleanup is to type
      // `entity` as a union and drop casts everywhere; out of scope here.)
      if (entity instanceof WireSegment) return wireSnapshot(entity);
      return null;
    }
    // Schematic annotation wrapper. Text annotations translate via their `loc`;
    // other graphics primitives (rect/polygon/…) don't expose a single anchor
    // point and aren't group-movable here.
    case EntityKind.Annotation: {
      if (!(entity instanceof Annotation)) return null;
      const { prim } = entity;
      if (prim.entityKind === EntityKind.Text) return locSnapshot(prim);
      return null;
    }
    default:
      return null;
  }
}

// A wire endpoint translation to apply during a group move. Captures the
// wire's original endpoints and which of them should follow the moving
// selection. Wires fully internal to the selection move both endpoints
// (rigid translation); boundary wires move only the endpoint that sits on a
// selected pin, so they stretch to stay connected to the unselected side.
interface WireMove {
  wire: WireSegment;
  origEndpoints: [Point, Point];
  origLabels: Array<graphics.Text>;
  moveStart: boolean;
  moveEnd: boolean;
}

// Plain wire endpoints, for the pure classifier below.
export interface WireEndpointPair {
  start: Point;
  end: Point;
}

// Which endpoints of a wire (referenced by index) should follow the move.
export interface WireMoveSpec {
  wireIndex: number;
  moveStart: boolean;
  moveEnd: boolean;
}

// Pure core of the connected-wire drag logic: given candidate wires and the
// world-coordinate keys of selected vs. external (unselected) pins, decide
// which wire endpoints should move with the selection.
//
// Wires are grouped into connected networks (segments joined at shared
// endpoints), then each network is classified by the pins it touches:
//   - touches a selected pin but no external pin -> fully internal, so the
//     whole network translates rigidly (both endpoints of every segment move).
//   - touches both a selected and an external pin -> boundary network, so only
//     the endpoints sitting on selected pins move; the rest stay put and the
//     wire stretches to preserve connectivity.
//   - touches no selected pin -> left untouched.
export function classifyConnectedWireMoves(
  wires: WireEndpointPair[],
  selectedPinKeys: Set<string>,
  externalPinKeys: Set<string>
): WireMoveSpec[] {
  if (selectedPinKeys.size === 0) return [];

  // The same partition the commit-time plan uses, so the preview cannot
  // disagree with it about what is attached to what.
  const specs: WireMoveSpec[] = [];
  for (const component of components(wires)) {
    // Classify the component by the pins its endpoints land on.
    let touchesSelected = false;
    let touchesExternal = false;
    for (const i of component) {
      for (const key of [pointKey(wires[i].start), pointKey(wires[i].end)]) {
        if (selectedPinKeys.has(key)) touchesSelected = true;
        else if (externalPinKeys.has(key)) touchesExternal = true;
      }
    }
    if (!touchesSelected) continue;

    for (const i of component) {
      // Internal net translates rigidly; boundary net moves only selected-pin
      // endpoints.
      const moveStart = touchesExternal
        ? selectedPinKeys.has(pointKey(wires[i].start))
        : true;
      const moveEnd = touchesExternal
        ? selectedPinKeys.has(pointKey(wires[i].end))
        : true;
      if (moveStart || moveEnd) {
        specs.push({ wireIndex: i, moveStart, moveEnd });
      }
    }
  }
  return specs;
}

// Find wires that should be dragged along when a group of instances/pins is
// moved. Wires explicitly in the selection are excluded here — they already
// have their own snapshots and translate rigidly via MoveSelection. The
// network classification itself lives in `classifyConnectedWireMoves`.
function computeConnectedWireMoves(
  schematic: Schematic,
  excludeWires: Set<WireSegment>
): WireMove[] {
  // Pins on the selected entities, and on everything else ("external").
  const selectedPinKeys = new Set<string>();
  const selectedInstances = new Set<Instance>();
  const selectedPins = new Set<SchPin>();
  for (const entity of schematic.selection) {
    if (entity instanceof Instance) {
      selectedInstances.add(entity);
      for (const loc of entity.getPinWorldLocations()) {
        selectedPinKeys.add(pointKey(loc));
      }
    } else if (entity instanceof SchPin) {
      selectedPins.add(entity);
      selectedPinKeys.add(pointKey(getSchPinWorldLocation(entity)));
    }
  }
  if (selectedPinKeys.size === 0) return [];

  const externalPinKeys = new Set<string>();
  for (const inst of schematic.instances) {
    if (selectedInstances.has(inst)) continue;
    for (const loc of inst.getPinWorldLocations()) {
      externalPinKeys.add(pointKey(loc));
    }
  }
  for (const pin of schematic.pins) {
    if (selectedPins.has(pin)) continue;
    externalPinKeys.add(pointKey(getSchPinWorldLocation(pin)));
  }

  // Candidate wires: everything not already moving as part of the selection.
  const wires = Array.from(schematic.wires.all()).filter(
    (w) => !excludeWires.has(w)
  );
  const endpoints: WireEndpointPair[] = wires.map((w) => ({
    start: w.start(),
    end: w.end(),
  }));

  const specs = classifyConnectedWireMoves(
    endpoints,
    selectedPinKeys,
    externalPinKeys
  );

  return specs.map(({ wireIndex, moveStart, moveEnd }) => ({
    wire: wires[wireIndex],
    origEndpoints: [endpoints[wireIndex].start, endpoints[wireIndex].end],
    origLabels: model.schematic.cloneWireLabels(wires[wireIndex].data.labels),
    moveStart,
    moveEnd,
  }));
}

// # MoveSelection
//
// Move all entities in the current selection set simultaneously.
// Supports both schematic entities (xform) and layout entities (LayoutXform).
//
// Pin positions as they stood when a move began, with the wire endpoints each
// pin holds.
//
// Captured up front because the plan works in pre-move coordinates: by commit
// time the selected entities have already been moved by the preview, and
// naming their pins from live positions would name them where they ended up.
//
// A pin claims wire endpoints inside its box, not just one exactly at its
// centre. That is the rule the circuit extractor uses
// (`ConnectedGroup.intersectsBbox`), so an endpoint a unit or two off a pin —
// which an import or a non-default grid can produce — is connected in the
// netlist. Matching only exact coordinates meant the editor would not carry
// such a wire along, quietly breaking a net the netlist thought was joined.
interface PinSnapshot {
  movingPinKeys: Set<string>;
  staticPinKeys: Set<string>;
  selectedInstances: Set<Instance>;
  // Identities for the debug net check.
  netPins: NetPin[];
  movingIds: Set<string>;
}

function capturePins(schematic: Schematic): PinSnapshot {
  const wireEndpoints = Array.from(schematic.wires.all()).flatMap((seg) => [
    seg.start(),
    seg.end(),
  ]);

  const movingPinKeys = new Set<string>();
  const staticPinKeys = new Set<string>();
  const netPins: NetPin[] = [];
  const movingIds = new Set<string>();

  // Key the pin itself, plus any wire endpoint the pin's box covers.
  const notePin = (
    id: string,
    loc: Point,
    bbox: model.Bbox | null,
    moving: boolean
  ) => {
    const keys = moving ? movingPinKeys : staticPinKeys;
    keys.add(pointKey(loc));
    if (bbox) {
      for (const pt of wireEndpoints) {
        if (model.Bbox.contains(bbox, pt)) keys.add(pointKey(pt));
      }
    }
    netPins.push({ id, loc });
    if (moving) movingIds.add(id);
  };

  const selectedInstances = new Set<Instance>();
  const selectedPins = new Set<SchPin>();
  for (const entity of schematic.selection) {
    if (entity instanceof Instance) selectedInstances.add(entity);
    else if (entity instanceof SchPin) selectedPins.add(entity);
  }

  const noteInstance = (inst: Instance, moving: boolean) => {
    const boxes = inst.getPinWorldBboxes();
    inst.getPinWorldLocations().forEach((loc, i) => {
      notePin(`${inst.data.name}.${i}`, loc, boxes[i] ?? null, moving);
    });
  };
  for (const inst of schematic.instances) {
    noteInstance(inst, selectedInstances.has(inst));
  }
  for (const pin of schematic.pins) {
    const loc = getSchPinWorldLocation(pin);
    notePin(`pin:${pin.data.portName}`, loc, null, selectedPins.has(pin));
  }

  return {
    movingPinKeys,
    staticPinKeys,
    selectedInstances,
    netPins,
    movingIds,
  };
}

export class MoveSelection extends ModeHandler {
  override movesGeometryLive = true;

  private readonly snapshots: Array<{ snapshot: MoveSnapshot; origLoc: Point }>;
  private readonly initialMousePos: Point;
  // Connected (but unselected) wires dragged along with the selection.
  private readonly wireMoves: WireMove[];

  // When true, this move is the placement step of a just-created entity:
  // committing folds the create + move into one undo entry, and cancelling
  // discards the created entity entirely (see commit/abort).
  private readonly placement: boolean;

  // Suppresses movement until the cursor leaves a small pixel radius around a
  // mouse-press. Passthrough for keyboard grabs and placement, which must track
  // the cursor immediately (see `enter`).
  private readonly dragThreshold: DragThreshold;

  // The delta most recently applied by `handleMouseMove`. Commit re-plans the
  // wires from the pre-move geometry, so it needs the offset rather than the
  // previewed result.
  private appliedDelta: Point = { x: 0, y: 0 };
  // Whether connected wires follow this move (false when the move-only
  // modifier was held).
  private readonly dragWires: boolean;
  // Pin positions from before the move; null outside schematics.
  private readonly pinSnapshot: PinSnapshot | null;

  constructor(
    private readonly editor: InteractionHost,
    snapshots: Array<{ snapshot: MoveSnapshot; origLoc: Point }>,
    initialMousePos: Point,
    wireMoves: WireMove[],
    placement = false,
    dragThreshold: DragThreshold = DragThreshold.passthrough(),
    dragWires = true,
    pinSnapshot: PinSnapshot | null = null
  ) {
    super();
    this.dragWires = dragWires;
    this.pinSnapshot = pinSnapshot;
    this.snapshots = snapshots;
    this.initialMousePos = initialMousePos;
    this.wireMoves = wireMoves;
    this.placement = placement;
    this.dragThreshold = dragThreshold;
    // A mouse drag commits on release; a keyboard grab or placement commits
    // on the next click. Say which, rather than always describing the grab.
    const message = placement
      ? "Place: left-click to confirm, right-click to cancel"
      : dragThreshold.fromMousePress
      ? "Drag: moving with connected wires"
      : "Grab: left-click to confirm, right-click to cancel";
    editor.overlays.setStatusMessage(message, "move-selection");
  }

  // Enter this mode. Filters the selection to movable entities.
  // Respects per-segment selection: if LayoutContent has selectedSegments
  // entries, only those segments are moved.
  // When `dragWires` is false (the move-only modifier was held), connected
  // wires are left in place instead of being dragged along.
  // `fromMousePress` should be true only when entered from a mouse-down drag
  // (so a plain click can be distinguished from a drag via the pixel
  // threshold). Keyboard grabs and placement leave it false and track the
  // cursor immediately.
  static enter(
    editor: InteractionHost,
    dragWires = true,
    placement = false,
    fromMousePress = false
  ): unknown {
    const { mousePos } = editor.uiState;
    const snapshots: Array<{ snapshot: MoveSnapshot; origLoc: Point }> = [];

    // Check for per-segment selection on LayoutContent
    const selectedSegments =
      editor.content instanceof LayoutContent
        ? editor.content.selectedSegments
        : undefined;

    for (const entity of editor.content.selection) {
      // Look up per-segment indices for this entity.
      // Safety: Map.get with a non-InstanceEntity key harmlessly returns undefined.
      const indices = selectedSegments?.get(entity as InstanceEntity) ?? null;
      const snapshot = trySnapshot(entity, indices);
      if (snapshot) {
        snapshots.push({
          snapshot,
          origLoc: Point.clone(snapshot.getLoc()),
        });
      }
    }

    if (snapshots.length === 0) return null;

    // In a schematic, also drag along wires connected to the moving entities.
    // Wires already in the selection have their own snapshots, so exclude them.
    let wireMoves: WireMove[] = [];
    if (dragWires && editor.content instanceof Schematic) {
      const selectedWires = new Set<WireSegment>();
      for (const entity of editor.content.selection) {
        if (entity instanceof WireSegment) selectedWires.add(entity);
      }
      wireMoves = computeConnectedWireMoves(editor.content, selectedWires);
    }

    // Placement: anchor at the entity's own location so the first mouse-move
    // snaps it onto the cursor rather than preserving a grab offset.
    //
    // The entity may have been seeded off-grid — a create RPC can be handed
    // any position, e.g. a drag-and-drop whose drop point is an arbitrary
    // float. Moves apply a *snapped delta* to the original location, so an
    // off-grid seed would survive placement and every later move. Re-seat the
    // snapshots onto the grid first, shifting them all by the anchor's own
    // correction so their relative spacing is preserved.
    let initialMousePos = Point.clone(mousePos.sch);
    if (placement && snapshots.length > 0) {
      const anchor = snapshots[0].origLoc;
      const snappedAnchor = editor.grid.nearestPoint(anchor);
      const correction = Point.sub(snappedAnchor, anchor);
      for (const entry of snapshots) {
        entry.origLoc = Point.add(entry.origLoc, correction);
      }
      initialMousePos = snappedAnchor;
    }

    // Arm the pixel threshold only for a genuine mouse-press drag. Placement
    // (which snaps onto the cursor immediately below) and keyboard grabs use a
    // passthrough so they track the cursor from the first move.
    const dragThreshold =
      fromMousePress && !placement
        ? new DragThreshold(Point.clone(mousePos.page))
        : DragThreshold.passthrough();

    const mode = new MoveSelection(
      editor,
      snapshots,
      initialMousePos,
      wireMoves,
      placement,
      dragThreshold,
      dragWires,
      editor.content instanceof Schematic ? capturePins(editor.content) : null
    );
    const result = editor.setMode(mode);
    // Snap onto the cursor immediately so it appears attached before any move.
    if (placement) mode.handleMouseMove();
    return result;
  }

  override handleMouseMove = () => {
    const { editor, snapshots, initialMousePos } = this;
    const { mousePos, modifiers } = editor.uiState;
    // Ignore sub-threshold cursor motion so a plain click doesn't move the selection.
    if (!this.dragThreshold.begun(mousePos.page)) return;
    // One entity snaps its absolute destination, so an off-grid one is
    // re-seated. A group snaps the shared delta instead, which keeps its
    // internal spacing (and leaves an off-grid member off-grid).
    const args = {
      initialMousePos,
      currentMousePos: mousePos.sch,
      shift: modifiers.shift,
      snap: editor.grid.nearestPoint,
    };
    const snappedDelta =
      snapshots.length === 1
        ? computeMovedToGrid({ ...args, origLoc: snapshots[0].origLoc })
        : computeMovedDelta(args);

    this.appliedDelta = snappedDelta;
    for (const { snapshot, origLoc } of snapshots) {
      snapshot.moveTo({
        x: origLoc.x + snappedDelta.x,
        y: origLoc.y + snappedDelta.y,
      });
    }

    // Drag connected wires along by the same delta. Labels are recomputed from
    // the wire's resulting geometry, so they track the wire whether it
    // translates rigidly (internal net) or stretches (boundary net).
    for (const { wire, origEndpoints, origLabels, moveStart, moveEnd } of this
      .wireMoves) {
      const [s, e] = origEndpoints;
      const newStart = moveStart
        ? { x: s.x + snappedDelta.x, y: s.y + snappedDelta.y }
        : { x: s.x, y: s.y };
      const newEnd = moveEnd
        ? { x: e.x + snappedDelta.x, y: e.y + snappedDelta.y }
        : { x: e.x, y: e.y };
      wire.update({
        endpoints: [newStart, newEnd],
        labels: model.schematic.recenterLabels(origLabels, newStart, newEnd),
      });
    }
  };

  // Keyboard-initiated move (G key): left-click to commit, right-click to cancel.
  override handleMouseDown = (e: MouseEvent) => {
    if (e.button === 0) {
      this.commit();
    } else {
      this.abort();
      this.editor.requestRedraw();
    }
  };

  // Drag-initiated move (mousedown on entity): commit on mouse-up.
  override handleMouseUp = () => {
    this.commit();
  };

  // Re-plan every wire the gesture touched, from the pre-move geometry.
  //
  // The preview stretches endpoints, which is fine as transient feedback but
  // is not a legal resting state: an off-axis drag leaves diagonal wires, and
  // the rest of the editor cannot handle those — hit-testing and connectivity
  // extraction both run through `Manhattan.calc` and give up on a diagonal.
  // This mode used to commit that stretch verbatim, so whether a drag produced
  // clean geometry depended on whether the clicked entity happened to be part
  // of a multi-selection.
  //
  // Restores the previewed geometry first, then plans from the originals and
  // applies the result. `planWireEdits` also keeps an endpoint that sits on a
  // stationary pin in place, so selecting a wire without the instance it
  // connects to no longer tears it off.
  private replanConnectedWires = (
    schematic: Schematic
  ): Array<changes.Single<changes.ChangeEntity>> => {
    // Undo the preview: wires dragged along, then any wire in the selection.
    for (const { wire, origEndpoints, origLabels } of this.wireMoves) {
      wire.update({
        endpoints: origEndpoints,
        labels: model.schematic.cloneWireLabels(origLabels),
      });
    }
    for (const { snapshot, origLoc } of this.snapshots) {
      if (snapshot.isWire) snapshot.moveTo(origLoc);
    }

    const targets = snapshotWires(schematic.render);
    const indexOf = new Map<WireSegment, number>();
    targets.segments.forEach((seg, i) => indexOf.set(seg, i));

    // Pin positions were captured at `enter`, before anything moved. The plan
    // works in pre-move coordinates plus a delta, and matches pins to wire
    // endpoints by exact position, so naming them from their live positions
    // here would name them where they ended up — nothing would match, and the
    // plan would come back empty.
    const pins = this.pinSnapshot;
    if (!pins) return [];

    const selectedWireIndices = new Set<number>();
    for (const entity of schematic.selection) {
      if (!(entity instanceof WireSegment)) continue;
      const index = indexOf.get(entity);
      if (index !== undefined) selectedWireIndices.add(index);
    }

    const obstacles = Array.from(schematic.instances)
      .filter((inst) => !pins.selectedInstances.has(inst))
      .map((inst) => inst.bbox);

    const plannedWires = targets.segments.map((seg) => ({
      start: seg.start(),
      end: seg.end(),
    }));
    const edits = planWireEdits({
      wires: plannedWires,
      movingPinKeys: pins.movingPinKeys,
      movingPins: pins.netPins
        .filter((pin) => pins.movingIds.has(pin.id))
        .map((pin) => pin.loc),
      staticPinKeys: pins.staticPinKeys,
      selectedWireIndices,
      delta: this.appliedDelta,
      obstacles,
      // A detour around a pin invents its crossover coordinate; snap it the
      // same way the drag itself snapped, so the bend lands on the user's grid.
      snap: this.editor.grid.nearestPoint,
    });
    const logged = applyWireEdits(schematic.render, targets, edits);
    // Two runs re-routing to the same anchor leave collinear geometry on top
    // of itself; neither route can see the other's, so collapse it afterwards.
    logged.push(...mergeOverlaps(schematic.render));
    auditWirePlan(
      {
        wires: plannedWires.map((wire) => [wire.start, wire.end]),
        movingPinKeys: pins.movingPinKeys,
        staticPinKeys: pins.staticPinKeys,
        delta: this.appliedDelta,
        pins: pins.netPins,
        movingIds: pins.movingIds,
      },
      edits,
      Array.from(schematic.wires.all())
    );
    return logged;
  };

  private commit = () => {
    const { editor, snapshots } = this;
    const singleChanges: Array<changes.Single<changes.ChangeEntity>> = [];

    // In a schematic the wires are re-planned below, so their snapshots must
    // not also log a move — that would double-count them in the undo entry.
    const replanWires =
      this.dragWires &&
      editor.content instanceof Schematic &&
      (this.appliedDelta.x !== 0 || this.appliedDelta.y !== 0);

    for (const { snapshot } of snapshots) {
      if (replanWires && snapshot.isWire) continue;
      const change = snapshot.logChange();
      if (change) singleChanges.push(change);
    }

    if (replanWires && editor.content instanceof Schematic) {
      singleChanges.push(...this.replanConnectedWires(editor.content));
    } else {
      // Layout content, or a move-only gesture: log the previewed endpoint
      // translations as they stand.
      for (const { wire, origEndpoints, origLabels } of this.wireMoves) {
        const current: [Point, Point] = [wire.start(), wire.end()];
        if (
          Point.eq(origEndpoints[0], current[0]) &&
          Point.eq(origEndpoints[1], current[1])
        ) {
          continue;
        }
        singleChanges.push(
          changes.update({
            target: wire,
            from: { endpoints: origEndpoints, labels: origLabels },
            to: {
              endpoints: current,
              labels: model.schematic.recenterLabels(
                origLabels,
                current[0],
                current[1]
              ),
            },
          })
        );
      }
    }

    if (singleChanges.length === 1) {
      editor.content.logChange(singleChanges[0] as changes.Single<never>);
    } else if (singleChanges.length > 1) {
      const combo = changes.Combo.fromArray(
        singleChanges as Array<changes.Single<never>>
      );
      if (combo.ok) editor.content.logChange(combo.val);
    }

    // Placement: fold the create's Add + this move into one undo entry — but
    // only if a move was logged (placing without moving leaves just the Add).
    if (this.placement && singleChanges.length > 0) {
      editor.content.combineRecentChanges(2);
    }

    // If any wire moved, junction dots are stale — refresh them.
    const movedWire =
      snapshots.some(({ snapshot }) => snapshot.isWire) ||
      this.wireMoves.length > 0;
    if (movedWire && editor.content instanceof Schematic) {
      editor.content.updateDots();
    }

    editor.overlays.clearStatusMessage("move-selection");
    editor.goUiIdle();
  };

  override abort = () => {
    // Placement cancel: discard the just-created entity entirely (no prior
    // position to restore) — a cancelled placement is a no-op.
    if (this.placement) {
      this.editor.content.discardCreatedSelection();
      this.editor.overlays.clearStatusMessage("move-selection");
      this.editor.goUiIdle();
      return;
    }
    // Restore all entities to their original positions.
    for (const { snapshot } of this.snapshots) {
      snapshot.restore();
    }
    // Restore any dragged-along wires.
    for (const { wire, origEndpoints, origLabels } of this.wireMoves) {
      wire.update({
        endpoints: [
          Point.clone(origEndpoints[0]),
          Point.clone(origEndpoints[1]),
        ],
        labels: model.schematic.cloneWireLabels(origLabels),
      });
    }
    this.editor.overlays.clearStatusMessage("move-selection");
    this.editor.goUiIdle();
  };
}

export class MoveLayoutXformable extends MoveBase<
  LayoutXformable,
  layout.LayoutXform
> {
  //
  // Enter this mode
  static enter(editor: InteractionHost, target: LayoutXformable) {
    const orig = target.getXform();
    const initialMousePos = Point.clone(editor.uiState.mousePos.sch);
    const origLoc = Point.clone(orig.loc);
    const move = new MoveLayoutXformable(
      editor,
      target,
      orig,
      origLoc,
      initialMousePos
    );
    return editor.setMode(move);
  }

  override updateData = (xform: layout.LayoutXform): unknown => {
    const { target } = this;
    return target.updateXform(xform);
  };

  override updateLoc = (loc: Point): unknown => {
    const { target } = this;
    const xform: layout.LayoutXform = target.getXform();
    return target.updateXform({ ...xform, loc });
  };

  override logChange = () => {
    const { editor, target, orig } = this;
    const to = target.getXform();
    if (layout.LayoutXform.eq(orig, to)) return; // No change

    // Notify the changeLog and platform of the change.
    editor.content.logChange(
      changes.update({
        target: target,
        from: { place: { kind: "scalar", xform: orig } },
        to: { place: { kind: "scalar", xform: to } },
      })
    );
  };

  // Handle keystrokes.
  override handleKey = (e: KeyboardEvent) => {
    const { target } = this;

    // All other UI states: check for "command" keystrokes.
    switch (e.key) {
      // FIXME: do we want deletion/ removal in this state? None for now.
      //   case Keys.Delete:
      //   case Keys.Backspace: {
      //     // Delete the selected target
      //     return editor.deleteSelected();
      //   }
      // Rotation & reflection
      // Note these *are not* committed to the changelog.
      // Mid-move rotations or reflections "count as part of" the move.
      case Keys.r: {
        const xform = target.getXform();
        return target.updateXform({
          ...xform,
          rotation: Rotation.next(xform.rotation ?? 0),
        });
      }
      case Keys.v: {
        const xform = target.getXform();
        return target.updateXform({
          ...xform,
          reflect: { ...xform.reflect, vert: !xform.reflect.vert },
        });
      }
      case Keys.h: {
        const xform = target.getXform();
        return target.updateXform({
          ...xform,
          reflect: { ...xform.reflect, horiz: !xform.reflect.horiz },
        });
      }
      default:
        // Note this *is not* an exhaustive check, on purpose.
        // There's lots of keys we don't care about!
        // Some day the logging should go away too.
        logger.log(`Key we dont use: '${e.key}'`);
    }
  };
}

// Create a snapshot for a segmented layout instance that moves only selected segments.
// Unselected segments stay in their original positions.
// Uses InstanceEntity's typed `inner` discriminated union — same approach as
// segmentedSnapshot above.
function selectedSegmentSnapshot(
  instance: InstanceEntity,
  selectedIndices: Set<number>
): MoveSnapshot | null {
  const { inner } = instance;
  if (inner.kind !== "segments" && inner.kind !== "array") return null;
  const drawer = inner.drawer;
  const { segments } = drawer.data.place;

  // See comment in segmentedSnapshot: for "array" kind, use drawer.update
  // to avoid recreating the inner and orphaning the segments reference.
  const isArray = inner.kind === "array";

  const origSegLocs = segments.map((s) =>
    s ? { x: s.loc.x, y: s.loc.y } : null
  );
  // Use first selected segment's loc as the representative position
  let firstSelectedLoc: Point = { x: 0, y: 0 };
  for (const idx of selectedIndices) {
    const seg = segments[idx];
    if (seg) {
      firstSelectedLoc = { x: seg.loc.x, y: seg.loc.y };
      break;
    }
  }
  const origLoc = { ...firstSelectedLoc };

  return {
    getLoc: () => {
      for (const idx of selectedIndices) {
        const seg = segments[idx];
        if (seg) return seg.loc;
      }
      return { x: 0, y: 0 };
    },
    moveTo: (loc: Point) => {
      const dx = loc.x - origLoc.x;
      const dy = loc.y - origLoc.y;
      for (const idx of selectedIndices) {
        const seg = segments[idx];
        const orig = origSegLocs[idx];
        if (seg && orig) {
          seg.loc = { x: orig.x + dx, y: orig.y + dy };
        }
      }
      if (isArray) {
        drawer.update(drawer.data);
      } else {
        instance.update(instance.data);
      }
    },
    logChange: () => {
      const moved = segments.some((seg, i) => {
        const orig = origSegLocs[i];
        return seg && orig && (seg.loc.x !== orig.x || seg.loc.y !== orig.y);
      });
      if (!moved) return null;
      return changes.update({
        target: instance,
        from: {
          place: {
            kind: "segments" as const,
            segments: origSegLocs.map((loc, i) => {
              const seg = segments[i];
              return seg && loc ? { ...seg, loc: { ...loc } } : null;
            }),
          },
        },
        to: {
          place: {
            kind: "segments" as const,
            segments: segments.map((seg) =>
              seg ? { ...seg, loc: { ...seg.loc } } : null
            ),
          },
        },
      });
    },
    restore: () => {
      for (const idx of selectedIndices) {
        const seg = segments[idx];
        const orig = origSegLocs[idx];
        if (seg && orig) {
          seg.loc = { x: orig.x, y: orig.y };
        }
      }
      if (isArray) {
        drawer.update(drawer.data);
      } else {
        instance.update(instance.data);
      }
    },
  };
}

//
// # MoveSegments
//
// Move selected segments of a segmented instance.
// Used when clicking/dragging individual segments (not the whole instance).
// Targets the parent InstanceEntity for change logging so getTargetKind works.
//
export class MoveSegments extends ModeHandler {
  override movesGeometryLive = true;

  private readonly snapshot: MoveSnapshot;
  private readonly initialMousePos: Point;
  private readonly origLoc: Point;

  // Suppresses movement until the cursor leaves a small pixel radius around the
  // press. MoveSegments is only entered from a mouse-drag, so the press point
  // is the cursor's page position at construction time.
  private readonly dragThreshold: DragThreshold;

  private constructor(
    private readonly editor: InteractionHost,
    snapshot: MoveSnapshot,
    initialMousePos: Point,
    origLoc: Point
  ) {
    super();
    this.snapshot = snapshot;
    this.initialMousePos = initialMousePos;
    this.origLoc = origLoc;
    this.dragThreshold = new DragThreshold(
      Point.clone(editor.uiState.mousePos.page)
    );
  }

  // Enter this mode for moving selected segments of an instance.
  static enter(
    editor: InteractionHost,
    instance: InstanceEntity,
    selectedIndices: Set<number>
  ): unknown {
    // Use instance.inner (typed discriminated union) directly — no unsafe cast needed.
    if (instance.inner.kind !== "segments" && instance.inner.kind !== "array") {
      // Should be unreachable: callers guard with `whatd_we_hit instanceof Segment`
      // which guarantees a segmented parent. Log if it ever fires.
      logger.error(
        "MoveSegments.enter called on non-segmented instance",
        instance.inner.kind
      );
      return null;
    }

    const snapshot = selectedSegmentSnapshot(instance, selectedIndices);
    if (!snapshot) {
      // Should be unreachable: selectedSegmentSnapshot only returns null for the
      // same kind check we just passed above.
      logger.error(
        "MoveSegments.enter: selectedSegmentSnapshot returned null unexpectedly"
      );
      return null;
    }
    const { mousePos } = editor.uiState;
    const origLoc = Point.clone(snapshot.getLoc());

    return editor.setMode(
      new MoveSegments(editor, snapshot, Point.clone(mousePos.sch), origLoc)
    );
  }

  override handleMouseMove = () => {
    const { editor, snapshot, initialMousePos, origLoc } = this;
    const { mousePos, modifiers } = editor.uiState;
    // Ignore sub-threshold cursor motion so a plain click doesn't move segments.
    if (!this.dragThreshold.begun(mousePos.page)) return;
    // One entity here (its selected segments move together), so snap the
    // absolute destination and re-seat it if it is off-grid.
    const snappedDelta = computeMovedToGrid({
      origLoc,
      initialMousePos,
      currentMousePos: mousePos.sch,
      shift: modifiers.shift,
      snap: editor.grid.nearestPoint,
    });

    snapshot.moveTo({
      x: origLoc.x + snappedDelta.x,
      y: origLoc.y + snappedDelta.y,
    });
  };

  override handleMouseUp = () => {
    this.commit();
  };

  private commit = () => {
    const { editor, snapshot } = this;
    const change = snapshot.logChange();
    if (change) {
      editor.content.logChange(change as changes.Single<never>);
    }
    editor.goUiIdle();
  };

  override abort = () => {
    this.snapshot.restore();
    this.editor.goUiIdle();
  };
}
